import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

@Directive({
  selector: '[appAutoTableHeight]',
  standalone: false
})
export class AutoTableHeightDirective implements OnInit, OnDestroy {
  @Input()
  public appAutoTableHeight = true;

  @Input()
  public appAutoTableHeightOffset = 0;

  @Input()
  public appAutoTableHeightDefault = '600px';

  @Input()
  public appAutoTableHeightContainerSelector: string | null = null;

  @Input()
  public appAutoTableHeightMin = 120;

  @Output()
  public appAutoTableHeightChange = new EventEmitter<string>();

  private _resizeObserver: ResizeObserver | null = null;
  private _observedElement: HTMLElement | null = null;
  private _lastHeight = '';

  constructor(
    private _elementRef: ElementRef,
    private _ngZone: NgZone
  ) {}

  public ngOnInit(): void {
    if (!this.appAutoTableHeight) {
      return;
    }

    const hostElement = this._elementRef.nativeElement as HTMLElement;
    
    // Find the container to observe: use custom selector, or find the grid cell/fieldset
    if (this.appAutoTableHeightContainerSelector) {
      this._observedElement = hostElement.closest(this.appAutoTableHeightContainerSelector) as HTMLElement | null;
    }
    if (!this._observedElement) {
      this._observedElement = (
        hostElement.closest('fieldset') ??
        hostElement.closest('.grid-stack-item') ??
        hostElement.closest('gridstack-item') ??
        hostElement.parentElement
      ) as HTMLElement | null ?? hostElement;
    }

    this._emitHeight(this.appAutoTableHeightDefault);

    this._resizeObserver = new ResizeObserver(entries => {
      const containerHeight = entries[0]?.contentRect?.height ?? 0;
      if (!containerHeight || containerHeight < this.appAutoTableHeightMin) {
        return;
      }

      // If explicit offset provided, use it. Otherwise, dynamically calculate offset by
      // measuring where the host element starts relative to the observed container.
      let computedHeight: number;
      if (this.appAutoTableHeightOffset > 0) {
        computedHeight = containerHeight - this.appAutoTableHeightOffset;
      } else {
        const containerRect = this._observedElement!.getBoundingClientRect();
        const hostRect = hostElement.getBoundingClientRect();
        const offsetFromTop = Math.max(0, Math.round(hostRect.top - containerRect.top));
        const minOffset = offsetFromTop + 10; // Small buffer for borders/padding
        computedHeight = containerHeight - minOffset;
      }

      if (computedHeight < this.appAutoTableHeightMin) {
        return;
      }

      this._emitHeight(`${computedHeight}px`);
    });

    this._resizeObserver.observe(this._observedElement);
  }

  public ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._observedElement = null;
  }

  private _emitHeight(height: string): void {
    if (height === this._lastHeight) {
      return;
    }

    this._lastHeight = height;
    this._ngZone.run(() => this.appAutoTableHeightChange.emit(height));
  }
}